import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface FinancialConnectionsSession {
  session_id: string;
  client_secret: string;
}

export interface FinancialConnectionsAccount {
  id: number;
  provider: string;
  institution_name: string;
  last4: string;
  status: string;
  /** Only bank rows dated on or after this day feed the P&L; null means all stored history. */
  track_transactions_from: string | null;
  created_at: string;
  updated_at: string;
}

export type BankTransactionClassification = 'pnl' | 'unknown' | 'skip';
export type BankTransactionClassifiedBy = 'rule' | 'llm' | 'customer';

/** A raw bank row synced from a connected account (not yet a P&L transaction). */
export interface BankTransaction {
  id: number;
  financial_connection_id: number;
  date: string;
  description: string;
  transaction_type: 'Credit' | 'Debit';
  amount: number;
  currency: string;
  status: 'pending' | 'processed' | 'void';
  /** null until the classifier has run on this row. */
  classification: BankTransactionClassification | null;
  classified_by: BankTransactionClassifiedBy | null;
  classified_at: string | null;
  /** The P&L transaction this row produced. Stays set even after that row is deleted. */
  financial_transaction_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface BankTransactionParams {
  page?: number;
  per_page?: number;
  status?: 'pending' | 'processed' | 'void';
  date_from?: string;
  date_to?: string;
}

export interface AddToProfitAndLossBody {
  monthly_finance_type_key: string;
  address_id: number;
  /** The bank row's own date when omitted. */
  period_start_date?: string;
}

@Injectable({ providedIn: 'root' })
export class FinancialConnectionsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api_url}api/v1/customer/financial-connections`;

  createSession(): Promise<FinancialConnectionsSession> {
    return firstValueFrom(
      this.http.post<FinancialConnectionsSession>(`${this.baseUrl}/session`, {})
    );
  }

  getConnectedAccounts(): Promise<FinancialConnectionsAccount[]> {
    return firstValueFrom(
      this.http.get<FinancialConnectionsAccount[]>(this.baseUrl)
    );
  }

  updateTrackTransactionsFrom(id: number, trackTransactionsFrom: string | null): Promise<FinancialConnectionsAccount> {
    return firstValueFrom(
      this.http.put<FinancialConnectionsAccount>(`${this.baseUrl}/${id}`, { track_transactions_from: trackTransactionsFrom })
    );
  }

  disconnectAccount(id: number, deleteTransactions = false): Promise<void> {
    const params = new HttpParams().set('delete_transactions', String(deleteTransactions));
    return firstValueFrom(
      this.http.delete<void>(`${this.baseUrl}/${id}`, { params })
    );
  }

  getBankTransactions(connectionId: number, params: BankTransactionParams = {}): Promise<BankTransaction[]> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') { continue; }
      httpParams = httpParams.set(key, String(value));
    }
    return firstValueFrom(
      this.http.get<BankTransaction[]>(`${this.baseUrl}/${connectionId}/transactions`, { params: httpParams })
    );
  }

  addToProfitAndLoss(connectionId: number, bankTransactionId: number, body: AddToProfitAndLossBody): Promise<any> {
    return firstValueFrom(
      this.http.post(`${this.baseUrl}/${connectionId}/transactions/${bankTransactionId}/add-to-profit-and-loss`, body)
    );
  }
}
